<template>
  <article
    v-if="product && product.name"
    class="product-item"
    @mouseover="mouseOver"
    @mouseout="mouseOut"
  >
    <component
      :is="getElement()"
      :to="`${product.url}${parameter}`"
      class="product-item__link product-item__link--image"
      :class="{
        'sqzl-click': product?.squeezely,
      }"
      :data-sqzl-personalization-id="product?.squeezely?.personalizationId"
      :data-sqzl-variant-id="product?.squeezely?.variantId"
      :data-sqzl-variant-revision="product?.squeezely?.variantRevision"
      @click="trackProductClick({ product: product, dataLayerOverrides: dataLayerOverrides })"
    >
      <div
        class="product-item__visual"
        :class="['item-' + imageType]"
      >
        <img
          class="product-item__image"
          :src="imageSrc"
          width="360"
          height="480"
          :loading="lazy ? 'lazy' : 'eager'"
          :fetchpriority="lazy ? 'low' : 'high'"
          :decoding="lazy ? 'async' : 'sync'"
          :alt="product.name || ''"
        >
        <div class="product-item__labels">
          <ProductLabelSale
            v-if="product.isSale && product.discount > 0"
            :prices="{
              fromPrice: hasFromPrice,
              minPrice: product.minPrice,
              regularPrice: product.regularPrice,
              finalPrice: product.finalPrice,
            }"
          />

          <ProductLabelNew
            v-else-if="product.isNew"
          />

          <ProductLabelMarketing
            v-if="product.marketingLabel"
            :product="product"
          />
        </div>
      </div>
    </component>
    <ProductToggleWishlist
      class="product-item__wishlist"
      :product="product"
    />
    <component
      :is="getElement()"
      :to="`${product.url}${parameter}`"
      class="product-item__link product-item__link--content"
      :class="{
        'sqzl-click': product?.squeezely,
      }"
      :data-sqzl-personalization-id="product?.squeezely?.personalizationId"
      :data-sqzl-variant-id="product?.squeezely?.variantId"
      :data-sqzl-variant-revision="product?.squeezely?.variantRevision"
      @click="trackProductClick({ product: product, dataLayerOverrides: dataLayerOverrides })"
    >
      <div class="product-item__details">
        <template v-if="product?.type !== 'grouped'">
          <span class="product-item__brand heading">
            {{ product?.brand }}
          </span>
          <span
            class="product-item__name"
          >
            {{ product.name.trim().replace(new RegExp(`\\s*${product?.brand}\\s*$`), '') }}
          </span>
        </template>
        <template v-else-if="product?.stl_brand">
          <div class="product-item__brand-container">
            <template
              v-for="(brand, index) in product?.stl_brand?.split(',')"
              :key="brand"
            >
              <span class="product-item__brand heading">
                {{ decodeURIComponent(brand.trim()) }}
              </span>
              <span
                v-if="index < product?.stl_brand?.split(',').length - 1"
                class="product-item__brand-divider"
              >/</span>
            </template>
          </div>
        </template>

        <span
          v-if="product?.type !== 'grouped' && !product?.stl_brand"
          class="product-item__sku"
        >
          <span>{{ $t('Art.Number: {sku}', { sku: product?.sku || '' }) }}</span>
        </span>

        <ProductPrice
          v-if="showPrice && product?.type !== 'grouped' && !product?.stl_brand"
          class="product-item__price"
          :prices="{
            fromPrice: hasFromPrice,
            minPrice: product.minPrice,
            regularPrice: product.regularPrice,
            finalPrice: product.finalPrice,
          }"
          :show-currency="true"
          :show-vat-tag="false"
        />
      </div>
    </component>

    <ProductRelatedColorProducts
      v-if="product?.relatedColorProducts?.length"
      :product="product"
      :show-title="false"
      :limit="2"
      :enable-popup="false"
      class="product-item__color-variants"
    />

    <template v-if="showCta">
      <button
        v-if="product.type === 'simple'"
        class="product-item__button button button--shop-flow button--icon-only button--small"
        type="button"
        :disabled="pending"
        @click="add"
      >
        <IconShoppingcart class="icon icon--cart" />
        <!-- <span>{{ $t('Add to cart') }}</span> -->
      </button>

      <AppLink
        v-else
        :to="`${product.url}${parameter}`"
        class="product-item__button button button--shop-flow button--icon-only button--small"
      >
        <IconArrow class="icon icon--arrow-right" />
        <!-- <span>{{ $t('View product') }}</span> -->
      </AppLink>
    </template>

    <slot />
  </article>
</template>

<script lang="ts" setup>
import type { EsProduct } from '~/types/elastic'
import type { DataLayerItem } from '~/composables/useDataLayer'
import { AppLink } from '#components'

const props = withDefaults(
  defineProps<{
    product: EsProduct
    lazy: boolean
    list?: string
    parameter?: string
    dataLayerOverrides?: Partial<DataLayerItem>
    linkable?: boolean
    showDescription?: boolean
    showPrice?: boolean
    showCta?: boolean
    forcePackshot?: boolean
  }>(),
  {
    list: 'other',
    parameter: '',
    dataLayerOverrides: undefined,
    linkable: true,
    showDescription: true,
    showPrice: true,
    showCta: false,
    forcePackshot: false,
  },
)

const { product } = toRefs(props)

const hover = ref(false)

const imageSrc = computed(() => {
  let imageSrc = props.forcePackshot && product?.value?.packshot ? product.value.packshot : product.value.image

  if (hover.value && (product.value.hover_image || (props.forcePackshot && product?.value?.packshot_hover_image))) {
    imageSrc = props.forcePackshot && product?.value?.packshot ? product.value.packshot_hover_image : product.value.hover_image
  }

  return getProductImage(imageSrc, 'f=rs:fit:525:700', product.value.name)
})

const imageType = computed(() => {
  let type = props.forcePackshot && product?.value?.packshot_kind_of_file ? product.value.packshot_kind_of_file : product.value.image_kind_of_file

  if (hover.value && (product.value.hover_image || (props.forcePackshot && product?.value?.packshot_hover_image))) {
    type = props.forcePackshot && product?.value?.packshot_kind_of_file ? product.value.packshot_hover_image_kind_of_file : product.value.hover_image_kind_of_file
  }

  return type || 'product'
})

const hasFromPrice = computed(() => {
  return product.value.type === 'configurable' && product.value.minPrice > 0 && product.value.minPrice !== product.value.regularPrice ? product.value.minPrice : undefined
})

function getElement() {
  return product.value.url && props.linkable ? AppLink : 'div'
}

function mouseOver() {
  hover.value = true
}

function mouseOut() {
  hover.value = false
}

const emit = defineEmits(['productClick'])
const { selectItem } = useDataLayer()

const trackProductClick = ({ product, dataLayerOverrides }: { product: EsProduct, dataLayerOverrides: any }) => {
  selectItem(product, dataLayerOverrides)
  emit('productClick', product)
}

const { addToCart } = useCart()
const pending = ref(false)

const add = async () => {
  pending.value = true

  await addToCart([{
    data: {
      quantity: 1,
      sku: product.value.sku,
    },
  }], false)

  pending.value = false
}
</script>

<style src="~layers/app/components/Product/Tile.css" />
